import type { TableSelectedCell } from '@/components/custom/erp';
import type { PhieuChiCellValue } from '../../../types';
import { useTableRows } from '@/components/custom/erp';
import type { FormFieldState } from '../../../hooks';

export type SelectedCellInfo = TableSelectedCell;

type DetailRowBase = {
  uuid: string;
  line?: number;
  [key: string]: unknown;
};

const ensureRowDefaults = <T extends DetailRowBase>(row: T, formState?: FormFieldState): T => {
  const defaults: Record<string, unknown> = {};

  if (formState?.dienGiai && (row.dien_giai === undefined || row.dien_giai === '')) {
    defaults.dien_giai = formState.dienGiai;
  }

  if (row.ma_loai_hd === undefined || row.ma_loai_hd === null) {
    defaults.ma_loai_hd = '0';
  }

  return {
    ...row,
    ...(defaults as Partial<T>)
  };
};

export function useDetailRows<T extends DetailRowBase>(
  initialRows: T[] = [],
  onChange?: (rows: T[]) => void,
  onFieldValueChangeProp?: (row: T, field: string, value: PhieuChiCellValue) => Partial<T>,
  formState?: FormFieldState
) {
  const {
    rows,
    setRows,
    selectedRowUuid,
    selectedRow,
    selectedCell,
    handleRowClick,
    handleCellClick,
    clearSelection,
    handleCellValueChange,
    handleAddRow: baseHandleAddRow,
    handleDeleteRow,
    handleCopyRow,
    handlePasteRow,
    handleMoveRow,
    setNonWritableFields,
    setPasteConfigs,
    isPasting
  } = useTableRows<T>({
    initialRows,
    defaultRows: 1,
    onChange,
    onFieldValueChange: (row, field, value) =>
      ({
        ...(onFieldValueChangeProp?.(row, field, value) ?? {})
      }) as Partial<T>,
    defaultField: 'dien_giai'
  });

  const handleAddRow = () => {
    baseHandleAddRow();
    setRows(prev => {
      if (!prev.length) return prev;
      const updated = [...prev];
      const lastIndex = updated.length - 1;
      updated[lastIndex] = ensureRowDefaults(updated[lastIndex], formState);
      return updated;
    });
  };

  return {
    rows,
    setRows,
    selectedRowUuid,
    selectedRow,
    selectedCell,
    handleRowClick,
    handleCellClick,
    clearSelection,
    handleCellValueChange,
    handleAddRow,
    handleDeleteRow,
    handleCopyRow,
    handlePasteRow,
    handleMoveRow,
    setNonWritableFields,
    setPasteConfigs,
    isPasting
  };
}
